import React from 'react';
import { RouteObject, createBrowserRouter } from 'react-router-dom';

import Layout from 'src/Layout/Layout';
import ErrorBoundary from 'src/Shared/ErrorBoundary';
import ErrorPage from '../Shared/Components/ErrorPage';

const Search = React.lazy(() => import('src/feature/Pages/Search'));
const MatterFinanceReport = React.lazy(() => import('src/feature/Reports/MatterFinanceReport/MatterFinanceReport'));
const Homepage = React.lazy(() => import('src/feature/Pages/Home/index'));
const ReportPage = React.lazy(() => import('src/feature/Pages/ReportPage'));

const SubscriptionPage = React.lazy(() => import('src/feature/Pages/Subscription'));
const AgedDisbursementReport = React.lazy(
  () => import('src/feature/Reports/AgedDisbursementsReport/AgedDisbursementReport'),
);
const DisbursementsReport = React.lazy(() => import('src/feature/Reports/DisbursementsReport/DisbursementsReport'));
const StaffReceiptedFundsReport = React.lazy(
  () => import('src/feature/Reports/StaffReceiptedFundsReport/StaffReceiptedFundsReport'),
);
const PreBillingSummaryReport = React.lazy(
  () => import('src/feature/Reports/PreBillingSummaryReport/PreBillingSummaryReport'),
);
const StaffInvoicedFundsReport = React.lazy(
  () => import('src/feature/Reports/StaffInvoicedFundsReport/StaffInvoicedFundsReport'),
);
const DailyTimeFeesByStaffReport = React.lazy(
  () => import('src/feature/Reports/DailyTimeFeesByStaffReport/DailyTimeFeesByStaffReport'),
);
const InvoicesReport = React.lazy(() => import('src/feature/Reports/InvoicesReport/InvoicesReport'));
const PrintInvoicesReport = React.lazy(() => import('src/feature/Reports/PrintInvoicesReport/PrintInvoicesReport'));
const PrintBankDepositSlips = React.lazy(
  () => import('src/feature/Reports/PrintBankDepositSlips/PrintBankDepositSlips'),
);
const CurrentConveyancingMatters = React.lazy(
  () => import('src/feature/Reports/CurrentConveyancingMatters/CurrentConveyancingMatters'),
);
const TrustInvestmentStatement = React.lazy(
  () => import('src/feature/Reports/TrustInvestmentStatement/TrustInvestmentStatement'),
);
const ClientDetailsSearch = React.lazy(() => import('src/feature/Reports/ClientDetailsSearch/ClientDetailsSearch'));
const MattersWithinCoolingOffPeriodReport = React.lazy(
  () => import('src/feature/Reports/MattersWithinCoolingOffPeriodReport/MattersWithinCoolingOffPeriodReport'),
);
const TrustAccountReceiptsCashBook = React.lazy(
  () => import('src/feature/Reports/TrustAccountReceiptsCashBook/TrustAccountReceiptsCashBook'),
);
const InvoiceVarianceReport = React.lazy(
  () => import('src/feature/Reports/InvoiceVarianceReport/InvoiceVarianceReport'),
);
const ExchangedMatters = React.lazy(() => import('src/feature/Reports/ExchangedMattersReport/ExchangedMattersReport'));
const TrustAuditReport = React.lazy(() => import('src/feature/Reports/TrustAuditReport/TrustAuditReport'));
const RapidPayInstalmentPlans = React.lazy(
  () => import('src/feature/Reports/RapidPayInstalmentReport/RapidPayInstalmentReport'),
);
const PowerAndEstateRegisterReport = React.lazy(
  () => import('src/feature/Reports/PowerAndEstateRegisterReport/PowerAndEstateRegisterReport'),
);
const PrintTrustStatement = React.lazy(() => import('src/feature/Reports/PrintTrustStatement/PrintTrustStatement'));
const MatterBalancesReport = React.lazy(() => import('src/feature/Reports/MatterBalancesReport/MatterBalancesReport'));
const ProtectedTrustFundsReport = React.lazy(
  () => import('src/feature/Reports/ProtectedTrustFundsReport/ProtectedTrustFundsReport'),
);
const MattersDueForExchange = React.lazy(
  () => import('src/feature/Reports/MattersDueForExchange/MattersDueForExchange'),
);
const StaffBudgetsReport = React.lazy(() => import('src/feature/Reports/StaffBudgetsReport/StaffBudgetsReport'));
const TrustTrialBalance = React.lazy(
  () => import('src/feature/Reports/TrustTrialBalanceReport/TrustTrialBalanceReport'),
);
const SettlementListDashboard = React.lazy(
  () => import('src/feature/Reports/SettlementListDashboard/SettlementListDashboard'),
);
const FirmExposureByClientReport = React.lazy(
  () => import('src/feature/Reports/FirmExposureByClientReport/FirmExposureByClientReport'),
);
const TrustJournalReport = React.lazy(() => import('src/feature/Reports/TrustJournalReport/TrustJournalReport'));
const DailyTimeFeesRecordedByStaffReport = React.lazy(
  () => import('src/feature/Reports/DailyTimeFeesRecordedByStaffReport/DailyTimeFeesRecordedByStaffReport'),
);
const LeaseMattersReport = React.lazy(() => import('src/feature/Reports/LeaseMattersReport/LeaseMattersReport'));
const MatterComplianceReport = React.lazy(
  () => import('src/feature/Reports/MatterComplianceReport/MatterComplianceReport'),
);
const NZAMLComplianceReport = React.lazy(() => import('src/feature/Reports/NZAMLComplianceReport'));
const AccountTrustEndOfMonth = React.lazy(
  () => import('src/feature/Reports/AccountTrustEndOfMonth/AccountTrustEndOfMonth'),
);
const AgentsReport = React.lazy(() => import('src/feature/Reports/AgentsReport/AgentsReport'));
const FirmOverviewReport = React.lazy(() => import('src/feature/Reports/FirmOverviewReport/FirmOverviewReport'));
const FeeContributionRecoveryReportByMatterType = React.lazy(
  () =>
    import('src/feature/Reports/FeeContributionRecoveryReportByMatterType/FeeContributionRecoveryReportByMatterType'),
);
const CombinedMatterLedgerSearch = React.lazy(
  () => import('src/feature/Reports/CombinedMatterLedgerSearch/CombinedMatterLedgerSearch'),
);
const MatterTasksReport = React.lazy(() => import('src/feature/Reports/MatterTasksReport/MatterTasksReport'));
const AgedWIPDisbursementsReport = React.lazy(
  () => import('src/feature/Reports/AgedWIPDisbursementsReport/AgedWIPDisbursementsReport'),
);
const RapidPayOfficeReceipts = React.lazy(
  () => import('src/feature/Reports/RapidPayOfficeReceipts/RapidPayOfficeReceipts'),
);
const FeeEarnerSummaryDownload = React.lazy(
  () => import('src/feature/Reports/FeeEarnerSummaryDownload/FeeEarnerSummaryDownload'),
);
const ReceiptAllocationReport = React.lazy(
  () => import('src/feature/Reports/ReceiptAllocationReport/ReceiptAllocationReport'),
);
const TrustLedgerSearch = React.lazy(() => import('src/feature/Reports/TrustLedger/TrustLedgerSearch'));
const OfficeReceiptsReport = React.lazy(() => import('src/feature/Reports/OfficeReceiptsReport/OfficeReceiptsReport'));
const GeneralLedgerMovementsReport = React.lazy(
  () => import('src/feature/Reports/GeneralLedgerMovementsReport/GeneralLedgerMovementsReport'),
);
const MattersCompletedReport = React.lazy(
  () => import('src/feature/Reports/CompletedMattersReport/CompletedMattersReport'),
);
const ClientOverviewReport = React.lazy(() => import('src/feature/Reports/ClientOverviewReport/ClientOverviewReport'));
const FeeContributionRecoveryReport = React.lazy(
  () => import('src/feature/Reports/FeeContributionRecoveryReport/FeeContributionRecoveryReport'),
);
const ConveyancingOverviewReport = React.lazy(
  () => import('src/feature/Reports/ConveyancingOverviewReport/ConveyancingOverviewReport'),
);
const RapidPayTrustReceipts = React.lazy(
  () => import('src/feature/Reports/RapidPayTrustReceipts/RapidPayTrustReceipts'),
);
const MattersDueForSettlement = React.lazy(
  () => import('src/feature/Reports/MattersDueForSettlement/MattersDueForSettlement'),
);
const LawConnectSharedInvoices = React.lazy(
  () => import('src/feature/Reports/LawConnectSharedInvoices/LawConnectSharedInvoices'),
);
const PrintTrustReceipts = React.lazy(() => import('src/feature/Reports/PrintTrustReceipts/PrintTrustReceipts'));
const TrustFundsUnclearedReport = React.lazy(
  () => import('src/feature/Reports/TrustFundsUnclearedReport/TrustFundsUnclearedReport'),
);
const PotentialTrustToOfficeTransferReport = React.lazy(
  () => import('src/feature/Reports/PotentialTrustToOfficeTransferReport/PotentialTrustToOfficeTransferReport'),
);
const AccrualDebtorGLValidation = React.lazy(
  () => import('src/feature/Reports/AccrualDebtorGLValidation/AccrualDebtorGLValidation'),
);
const MattersOpenedByStaffChart = React.lazy(
  () => import('src/feature/Reports/MattersOpenedByStaffChart/MattersOpenedByStaffChart'),
);
const MatterBasedActivityRecordingVariance = React.lazy(
  () => import('src/feature/Reports/MatterBasedActivityRecordingVariance/MatterBasedActivityRecordingVariance'),
);
const TrustBankReconciliation = React.lazy(
  () => import('src/feature/Reports/TrustBankReconciliation/TrustBankReconciliation'),
);
const TrustTrialBalanceByClientReport = React.lazy(
  () => import('src/feature/Reports/TrustTrialBalanceByClientReport/TrustTrialBalanceByClientReport'),
);
const GeneralTrustEndOfMonth = React.lazy(
  () => import('src/feature/Reports/GeneralTrustEndOfMonth/GeneralTrustEndOfMonth'),
);
const WorkObtainedReport = React.lazy(() => import('src/feature/Reports/WorkObtainedReport/WorkObtainedReport'));
const DocumentRegisterReport = React.lazy(
  () => import('src/feature/Reports/DocumentRegisterReport/DocumentRegisterReport'),
);
const TrustInvestmentListingOfAccounts = React.lazy(
  () => import('src/feature/Reports/TrustInvestmentListingOfAccounts/TrustInvestmentListingOfAccounts'),
);
const InactiveMattersReport = React.lazy(
  () => import('src/feature/Reports/InactiveMattersReport/InactiveMattersReport'),
);
const MattersOpenedReport = React.lazy(() => import('src/feature/Reports/MattersOpenedReport/MattersOpenedReport'));
const MattersByMatterTypeChart = React.lazy(
  () => import('src/feature/Reports/MattersByMatterTypeChart/MattersByMatterTypeChart'),
);
const OfficePaymentsReport = React.lazy(() => import('src/feature/Reports/OfficePaymentsReport/OfficePaymentsReport'));
const ControlledMoneyStatement = React.lazy(
  () => import('src/feature/Reports/ControlledMoneyStatement/ControlledMoneyStatement'),
);
const ControlledMoneyMovementLedger = React.lazy(
  () => import('src/feature/Reports/ControlledMoneyMovementLedger/ControlledMoneyMovementLedger'),
);
const TrustInvestmentLedger = React.lazy(
  () => import('src/feature/Reports/TrustInvestmentLedger/TrustInvestmentLedger'),
);
const AppointmentsReport = React.lazy(() => import('src/feature/Reports/AppointmentsReport/AppointmentsReport'));
const AnticipatedPaymentsReport = React.lazy(
  () => import('src/feature/Reports/AnticipatedPaymentsReport/AnticipatedPaymentsReport'),
);
const PracticeOverviewReport = React.lazy(
  () => import('src/feature/Reports/PracticeOverviewReport/PracticeOverviewReport'),
);
const TrustBankAccountRegisterReport = React.lazy(
  () => import('src/feature/Reports/TrustBankAccountRegisterReport/TrustBankAccountRegisterReport'),
);
const CostAgreementReport = React.lazy(() => import('src/feature/Reports/CostAgreementReport/CostAgreementReport'));
const DormantMattersWithTrustBalances = React.lazy(
  () => import('src/feature/Reports/DormantMattersWithTrustBalances/DormantMattersWithTrustBalances'),
);
const RapidPayReferenceLookup = React.lazy(
  () => import('src/feature/Reports/RapidPayReferenceLookup/RapidPayReferenceLookup'),
);
const BillingRealisationSummary = React.lazy(
  () => import('src/feature/Reports/BillingRealisationSummary/BillingRealisationSummary'),
);
const TrustOverdrawnReport = React.lazy(() => import('src/feature/Reports/TrustOverdrawnReport/TrustOverdrawnReport'));
const StampDutyDueReport = React.lazy(() => import('src/feature/Reports/StampDutyDueReport/StampDutyDueReport'));
const AgedDebtorsReport = React.lazy(() => import('src/feature/Reports/AgedDebtorsReport/AgedDebtorsReport'));
const PowerMoneyListingOfAccountsReport = React.lazy(
  () => import('src/feature/Reports/PowerMoneyListingOfAccountsReport/PowerMoneyListingOfAccountsReport'),
);
const PrintReceiptsReport = React.lazy(() => import('src/feature/Reports/PrintReceiptsReport/PrintReceiptsReport'));

const CompletedSettlementsByStaff = React.lazy(
  () => import('src/feature/Reports/CompletedSettlementsByStaff/CompletedSettlementsByStaff'),
);
const MatterListReport = React.lazy(() => import('src/feature/Reports/MatterListReport/MatterListReport'));
const TransitRegisterReport = React.lazy(
  () => import('src/feature/Reports/TransitRegisterReport/TransitRegisterReport'),
);
const FloatReport = React.lazy(() => import('src/feature/Reports/FloatReport'));
const ArchivedMattersReport = React.lazy(
  () => import('src/feature/Reports/ArchivedMattersReport/ArchivedMattersReport'),
);
const StaffTimeFeeActivitySummary = React.lazy(
  () => import('src/feature/Reports/StaffTimeFeeActivitySummary/StaffTimeFeeActivitySummary'),
);
const ExchangesAndSettlementDashboard = React.lazy(
  () => import('src/feature/Reports/ExchangesAndSettlementDashboard/ExchangesAndSettlementDashboard'),
);
const CriticalDatesReport = React.lazy(() => import('src/feature/Reports/CriticalDatesReport/CriticalDatesReport'));
const StaffDetailsReport = React.lazy(() => import('src/feature/Reports/StaffDetailsReport/StaffDetailsReport'));
const TrustBankDepositSlip = React.lazy(() => import('src/feature/Reports/TrustBankDepositSlip/TrustBankDepositSlip'));
const TrustAnticipatedDepositReport = React.lazy(
  () => import('src/feature/Reports/TrustAnticipatedDeposit/TrustAnticipatedDeposit'),
);
const StatementOfAccountSummary = React.lazy(
  () => import('src/feature/Reports/StatementOfAccountSummary/StatementOfAccountSummary'),
);
const TrustAccountPaymentsCashBook = React.lazy(
  () => import('src/feature/Reports/TrustAccountPaymentsCashBook/TrustAccountPaymentsCashBook'),
);
const ControlledMoneyListingOfAccountsReport = React.lazy(
  () => import('src/feature/Reports/ControlledMoneyListingOfAccountsReport/ControlledMoneyListingOfAccountsReport'),
);
const PreBillingGuideReport = React.lazy(
  () => import('src/feature/Reports/PreBillingGuidesReport/PrebillingGuideReport'),
);
const ExchangesAndSettlementReport = React.lazy(
  () => import('src/feature/Reports/ExchangesAndSettlementReport/ExchangesAndSettlementReport'),
);
const WorkObtainedChart = React.lazy(() => import('src/feature/Reports/WorkObtainedChart/WorkObtainedChart'));
const CardDetailsSearch = React.lazy(() => import('src/feature/Reports/CardDetailsSearch/CardDetailsSearch'));
const StatementOfAccountDebtors = React.lazy(
  () => import('src/feature/Reports/StatementOfAccountDebtors/StatementOfAccountDebtors'),
);
const ControlledMoneyControlledAccount = React.lazy(
  () => import('src/feature/Reports/ControlledMoneyControlledAccount/ControlledMoneyControlledAccount'),
);
const FeeEarnerOverviewReport = React.lazy(
  () => import('src/feature/Reports/FeeEarnerOverviewReport/FeeEarnerOverviewReport'),
);

const TimeFeesRecByStaffReport = React.lazy(
  () => import('src/feature/Reports/TimeFeesRecByStaffReport/TimeFeesRecByStaffReport'),
);
const MatterStatusReport = React.lazy(() => import('src/feature/Reports/MatterStatusReport/MatterStatusReport'));

const PowerMoneyLedgerReport = React.lazy(
  () => import('src/feature/Reports/PowerMoneyLedgerReport/PowerMoneyLedgerReport'),
);

const TimeAndFeeAnalysisReport = React.lazy(
  () => import('src/feature/Reports/TimeAndFeeAnalysisReport/TimeAndFeeAnalysisReport'),
);

const AgedWIPReport = React.lazy(() => import('src/feature/Reports/AgedWIP/AgedWIPReport'));

const PreviousEndOfMonth = React.lazy(() => import('src/feature/Reports/PreviousTrustEndOfMonth'));

const BillingJournals = React.lazy(() => import('src/feature/Reports/BillingJournals'));
const BilledAndUnbilledByStaff = React.lazy(() => import('src/feature/Reports/BilledAndUnbilledByStaff'));

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: Layout,
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        index: true,
        Component: Homepage,
      },
      {
        path: 'search',
        Component: Search,
      },
      {
        path: 'subscription',
        Component: SubscriptionPage,
      },
      {
        path: 'officeMenu',
        // @ts-ignore
        Component: ReportPage,
        // @ts-ignore
        ldConfig: {
          title: 'Office Report',
        },
        children: [
          {
            path: 'officeMatterFinancialSummary',
            Component: MatterFinanceReport,
          },
          {
            path: 'officeAgedWipReport',
            Component: AgedWIPReport,
          },
          {
            path: 'agedUnbilledReport',
            Component: AgedWIPDisbursementsReport,
          },
          {
            path: 'officeAgedDebtorsReport',
            Component: AgedDebtorsReport,
          },
          {
            path: 'officeAgedPayments',
            Component: AgedDisbursementReport,
          },
          {
            path: 'disbursementsReport',
            Component: DisbursementsReport,
          },
          {
            path: 'preBillingSummary',
            Component: PreBillingSummaryReport,
          },
          {
            path: 'officeInvoicesReport',
            Component: InvoicesReport,
          },
          {
            path: 'officeInvoiceheadermultiple',
            Component: PrintInvoicesReport,
          },
          {
            path: 'officeBankDepositSlipmultiple',
            Component: PrintBankDepositSlips,
          },
          {
            path: 'matterLedgerWithBalances',
            Component: CombinedMatterLedgerSearch,
          },
          {
            path: 'officeReceiptmultiple',
            Component: PrintReceiptsReport,
          },
          {
            path: 'WEBOfficeStatementSummaryR1064',
            Component: StatementOfAccountSummary,
          },
          {
            path: 'APPOfficeStatementDebtorR1066',
            Component: StatementOfAccountDebtors,
          },
          {
            path: 'officeReceipts',
            Component: OfficeReceiptsReport,
          },
          {
            path: 'officePaymentsReport',
            Component: OfficePaymentsReport,
          },
          {
            path: 'preBillingGuides',
            Component: PreBillingGuideReport,
          },
          {
            path: 'officeClientExposure',
            Component: FirmExposureByClientReport,
          },
          {
            path: 'officeAnticipatedPayments',
            Component: AnticipatedPaymentsReport,
          },
          {
            path: 'officeReceiptsRapidPay',
            Component: RapidPayOfficeReceipts,
          },
          {
            path: 'btiRefLookup',
            Component: RapidPayReferenceLookup,
          },
          {
            path: 'officeGLSummary',
            Component: GeneralLedgerMovementsReport,
          },
          {
            path: 'officeMatterFinancialSummaryTotals',
            Component: MatterBalancesReport,
          },
          {
            path: 'accrualDebtorGLValidation',
            Component: AccrualDebtorGLValidation,
          },
          {
            path: 'billedAndUnbilledByStaff',
            Component: BilledAndUnbilledByStaff,
          },
        ],
      },
      {
        path: 'dashboardMenu',
        // @ts-ignore
        Component: ReportPage,
        // @ts-ignore
        ldConfig: {
          title: 'Dashboard Report',
        },
        children: [
          {
            path: 'exchangesSettlements',
            Component: ExchangesAndSettlementDashboard,
          },
          {
            path: 'settlementListbyStaff',
            Component: CompletedSettlementsByStaff,
          },
          {
            path: 'settlementList',
            Component: SettlementListDashboard,
          },
        ],
      },
      {
        path: 'mattersAndClientsMenu',
        Component: ReportPage,
        // @ts-ignore
        // @ts-ignore
        ldConfig: {
          title: 'Matters and Client',
        },
        children: [
          {
            path: 'appointments',
            Component: AppointmentsReport,
          },
          {
            path: 'clientAndCardsMenu/cardDetails',
            Component: CardDetailsSearch,
          },
          {
            path: 'clientAndCardsMenu/clientDetails',
            Component: ClientDetailsSearch,
          },
          {
            path: 'conveyancingMenu/agentsReport',
            Component: AgentsReport,
          },
          {
            path: 'conveyancingMenu/mattersCurrentConveyancingwithaddress',
            Component: CurrentConveyancingMatters,
          },
          {
            path: 'conveyancingMenu/matterswithincoolingoffperiod',
            Component: MattersWithinCoolingOffPeriodReport,
          },
          {
            path: 'conveyancingMenu/matterduesettlement',
            Component: MattersDueForSettlement,
          },
          {
            path: 'conveyancingMenu/mattersduetobeexchanged',
            Component: MattersDueForExchange,
          },
          {
            path: 'conveyancingMenu/mattersExchanged',
            Component: ExchangedMatters,
          },
          {
            path: 'documentRegisterWeb',
            Component: DocumentRegisterReport,
          },
          {
            path: 'matterList',
            Component: MatterListReport,
          },
          {
            path: 'matterLease',
            Component: LeaseMattersReport,
          },
          {
            path: 'officeCostAgreementRequired',
            Component: CostAgreementReport,
          },
          {
            path: 'mattersstampdutydue',
            Component: StampDutyDueReport,
          },
          {
            path: 'mattersinactivityreport',
            Component: InactiveMattersReport,
          },
          {
            path: 'workObtainedByReferrer',
            Component: WorkObtainedReport,
          },
          {
            path: 'mattersArchived',
            Component: ArchivedMattersReport,
          },
          {
            path: 'powerAndEstateRegisterReport',
            Component: PowerAndEstateRegisterReport,
          },
          {
            path: 'officeClientOverview',
            Component: ClientOverviewReport,
          },
          {
            path: 'matterCriticalDates',
            Component: CriticalDatesReport,
          },
          {
            path: 'mattersOpened',
            Component: MattersOpenedReport,
          },
          {
            path: 'mattersCompleted',
            Component: MattersCompletedReport,
          },
          {
            path: 'complianceReport',
            Component: MatterComplianceReport,
            // exclusive to the UK
          },
          {
            path: 'nzamlCompliance',
            Component: NZAMLComplianceReport,
            // exclusive to the NZ
          },
          {
            path: 'mattersTask',
            Component: MatterTasksReport,
          },
        ],
      },
      {
        path: 'managementMenu',
        Component: ReportPage,

        // @ts-ignore
        ldConfig: {
          title: 'Management Report',
        },
        children: [
          {
            path: 'AutoTimeMenu/billablehoursV2',
            Component: MatterBasedActivityRecordingVariance,
          },
          {
            path: 'AutoTimeMenu/invoiceSharingDetails',
            Component: LawConnectSharedInvoices,
          },
          {
            path: 'officeTimeFeesByStaff',
            Component: DailyTimeFeesByStaffReport,
          },
          {
            path: 'officeTimeRecByStaff',
            Component: TimeFeesRecByStaffReport,
          },
          {
            path: 'mattersByStatusChart',
            Component: MatterStatusReport,
          },
          {
            path: 'mattersByTypeChart',
            Component: MattersByMatterTypeChart,
          },
          {
            path: 'manStaffReceiptedFunds',
            Component: StaffReceiptedFundsReport,
          },
          {
            path: 'manStaffInvoicedFunds',
            Component: StaffInvoicedFundsReport,
          },
          {
            path: 'billingRealisationReport',
            Component: BillingRealisationSummary,
          },
          {
            path: 'ManFeeContributionsAndRecovery',
            Component: FeeContributionRecoveryReport,
          },
          {
            path: 'feeContributionsAndRecoveryByMatterType',
            Component: FeeContributionRecoveryReportByMatterType,
          },
          {
            path: 'officeStaffTimeFeeActivitySummary',
            Component: StaffTimeFeeActivitySummary,
          },
          {
            path: 'practiceOverview',
            Component: PracticeOverviewReport,
          },
          {
            path: 'invoiceVarianceReport',
            Component: InvoiceVarianceReport,
          },
          {
            path: 'rapidPayInstalmentPlan',
            Component: RapidPayInstalmentPlans,
          },
          {
            path: 'officeStaffDetails',
            Component: StaffDetailsReport,
          },
          {
            path: 'WEBStaffBudgetsR1215',
            Component: StaffBudgetsReport,
          },
          {
            path: 'ExchangesAndSettlementsOverTimeChart',
            Component: ExchangesAndSettlementReport,
          },
          {
            path: 'ExchangesAndSettlementsOverTimeChartUs',
            Component: ExchangesAndSettlementReport,
          },
          {
            path: 'officeFirmOverview',
            Component: FirmOverviewReport,
          },
          {
            path: 'officeFeeEarnerOverview',
            Component: FeeEarnerOverviewReport,
          },
          {
            path: 'conveyancingOverview',
            Component: ConveyancingOverviewReport,
          },
          {
            path: 'workObtainedChart',
            Component: WorkObtainedChart,
          },
          {
            path: 'receiptAllocationReport',
            Component: ReceiptAllocationReport,
          },
          {
            path: 'officeTimeFeesByMatter',
            Component: DailyTimeFeesRecordedByStaffReport,
          },
          {
            path: 'analysisTimeAndFees',
            Component: TimeAndFeeAnalysisReport,
          },
          {
            path: 'officeFeeEarnerSummary',
            Component: FeeEarnerSummaryDownload,
          },
          {
            path: 'mattersOpenedRespAndActChart',
            Component: MattersOpenedByStaffChart,
          },
          {
            path: 'billingJournals',
            Component: BillingJournals,
          },
        ],
      },

      {
        path: 'trustMenu',
        Component: ReportPage,
        // @ts-ignore
        ldConfig: {
          title: 'Trust Report',
        },
        children: [
          {
            path: 'trustEndofMonthMenu/trustEomGeneral',
            Component: GeneralTrustEndOfMonth,
          },
          {
            path: 'trustEndofMonthMenu/trustEom',
            Component: AccountTrustEndOfMonth,
          },
          {
            path: 'trustEndofMonthMenu/trustEomBrowser',
            Component: PreviousEndOfMonth,
          },
          {
            path: 'WEBTrustLedgerR1214',
            Component: TrustLedgerSearch,
          },
          {
            path: 'trustTrialBalance',
            Component: TrustTrialBalance,
          },
          {
            path: 'controlledMoneyMenu/trustCtrlMoneyListOfAccounts',
            Component: ControlledMoneyListingOfAccountsReport,
          },
          {
            path: 'controlledMoneyMenu/trustCtrlMoneyStmtMultiple',
            Component: ControlledMoneyStatement,
          },
          {
            path: 'controlledMoneyMenu/trustCtrlMoneyLedgerMultiple',
            Component: ControlledMoneyMovementLedger,
          },
          {
            path: 'trustInvestmentMenu/trustInvestListOfAccounts',
            Component: TrustInvestmentListingOfAccounts,
          },
          {
            path: 'trustInvestmentMenu/trustInvestLedgerMultiple',
            Component: TrustInvestmentLedger,
          },
          {
            path: 'trustInvestmentMenu/trustInvestStmtMultiple',
            Component: TrustInvestmentStatement,
          },
          {
            path: 'trustAuditReport',
            Component: TrustAuditReport,
          },
          {
            path: 'trustBankDepositSlipMultiple',
            Component: TrustBankDepositSlip,
          },
          {
            path: 'trustProtectedFundsReport',
            Component: ProtectedTrustFundsReport,
          },
          {
            path: 'trustStatementMultiple',
            Component: PrintTrustStatement,
          },
          {
            path: 'trustJournalReport',
            Component: TrustJournalReport,
          },
          {
            path: 'webTrustOverdrawnR1153',
            Component: TrustOverdrawnReport,
          },
          {
            path: 'bankAccountRegister',
            Component: TrustBankAccountRegisterReport,
          },
          {
            path: 'anticipatedDeposits',
            Component: TrustAnticipatedDepositReport,
          },
          {
            path: 'trustCashReceiptsReport',
            Component: TrustAccountReceiptsCashBook,
          },
          {
            path: 'trustCashPaymentsReport',
            Component: TrustAccountPaymentsCashBook,
          },
          {
            path: 'trustRapidPayReceiptsReport',
            Component: RapidPayTrustReceipts,
          },
          {
            path: 'trustReceiptMultiple',
            Component: PrintTrustReceipts,
          },
          {
            path: 'trustToOfficeTransfer',
            Component: PotentialTrustToOfficeTransferReport,
          },
          {
            path: 'trustUnclearedFunds',
            Component: TrustFundsUnclearedReport,
          },
          {
            path: 'trustTrialBalanceByClient',
            Component: TrustTrialBalanceByClientReport,
          },
          {
            path: 'officeDormantBalances',
            Component: DormantMattersWithTrustBalances,
          },
          {
            path: 'powerMoneyMenu/trustPowerMoneyListOfAccounts',
            Component: PowerMoneyListingOfAccountsReport,
          },
          {
            path: 'powerMoneyMenu/trustPowerMoneyLedgerMultiple',
            Component: PowerMoneyLedgerReport,
          },
          {
            path: 'TrustBankRec',
            Component: TrustBankReconciliation,
          },
          {
            path: 'controlledMoneyMenu/trustCtrlMoneyCtrlAccount',
            Component: ControlledMoneyControlledAccount,
          },
          {
            path: 'transitRegisterReport',
            Component: TransitRegisterReport,
          },
          {
            path: 'floatReport',
            Component: FloatReport,
          },
        ],
      },
      {
        path: '*',
        Component: ErrorPage,
      },
    ],
  },
];

// if (isBeta()) {
//   // @ts-ignore
//   routes[0].children = routes[0].children.filter((item) => !item.hideInBeta);
// }
const router = createBrowserRouter(routes);

export default router;
